import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAppDispatch } from '../../../hooks';
import {
  exportOrder,
  exportUsers,
  sendFoodItemsReport,
} from '../../../store/slices';
import { formatDate } from '../../../helpers';
import { brandApi, pilotsApi } from '../../../api';
import { toast } from 'react-toastify';

const CalendarIcon = () => (
  <svg width={16} height={16} viewBox="0 0 16 16" fill="none" className="text-neutral-500">
    <path
      d="M5.333 1.333v2M10.667 1.333v2M2.333 6.053h11.334M14 5.667v5.666c0 2-1 3.334-3.333 3.334H5.333C3 14.667 2 13.333 2 11.333V5.667c0-2 1-3.334 3.333-3.334h5.334C13 2.333 14 3.667 14 5.667z"
      stroke="currentColor"
      strokeWidth={1.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const datePickerInputClass =
  'w-full border border-slate-medium bg-subtle rounded-lg pl-3 pr-10 py-3 text-sm text-text placeholder:text-text-placeholder focus:outline-none focus:bg-white focus:border-primary';

export const ExportCsv = ({
  module = 'orders',
  toggle,
}: {
  module?: 'orders' | 'users' | 'pilots' | 'reviews';
  toggle: () => void;
}) => {
  const dispatch = useAppDispatch();

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const [filter, setFilter] = React.useState({
    from: module === 'pilots' ? undefined : formatDate(yesterday),
    till: module === 'pilots' ? undefined : formatDate(today),
  });

  const exportOrders = async () => {
    if (module === 'users') {
      return dispatch(exportUsers({ payload: filter, cb: toggle }));
    }

    if (module === 'pilots') {
      try {
        await pilotsApi.exportPilots({
          startDate: filter.from,
          endDate: filter.till,
        });

        toast.success(
          'CSV file is being generated. You will receive an email shortly.',
        );
        return toggle();
      } catch (error) {
        return toast.error('An error occurred while generating the CSV file.');
      }
    }

    if (module === 'reviews') {
      try {
        await brandApi.exportReviews({
          startDate: filter.from,
          endDate: filter.till,
        });
        toast.success(
          'CSV file is being generated. You will receive an email shortly.',
        );

        return toggle();
      } catch (error) {
        return toast.error('An error occurred while generating the CSV file.');
      }
    }

    dispatch(exportOrder({ payload: filter, cb: toggle }));
  };

  const exportFoodItems = () => {
    dispatch(sendFoodItemsReport({ payload: filter, cb: toggle }));
  };

  const handleDateChange = (name: 'from' | 'till', date: Date | null) => {
    setFilter({ ...filter, [name]: date ? formatDate(date) : undefined });
  };

  return (
    <div className="bg-white p-6 rounded-xl w-[420px]">
      <p className="semibold text-lg text-text mb-5">Export {module} as CSV</p>

      <div className="flex flex-col gap-5">
        <div className="flex flex-col">
          <label className="medium text-sm text-text mb-1.5" htmlFor="from">
            From
          </label>
          <div className="relative">
            <DatePicker
              id="from"
              selected={filter.from ? new Date(filter.from) : null}
              onChange={(date) => handleDateChange('from', date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="DD/MM/YYYY"
              className={datePickerInputClass}
              wrapperClassName="w-full block"
              maxDate={filter.till ? new Date(filter.till) : undefined}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <CalendarIcon />
            </span>
          </div>
        </div>

        <div className="flex flex-col">
          <label className="medium text-sm text-text mb-1.5" htmlFor="till">
            To
          </label>
          <div className="relative">
            <DatePicker
              id="till"
              selected={filter.till ? new Date(filter.till) : null}
              onChange={(date) => handleDateChange('till', date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="DD/MM/YYYY"
              className={datePickerInputClass}
              wrapperClassName="w-full block"
              minDate={filter.from ? new Date(filter.from) : undefined}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <CalendarIcon />
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          className="px-5 py-2.5 text-sm medium border border-slate-medium rounded-lg text-text bg-white hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={exportOrders}
          disabled={!filter.from || !filter.till}
          className="px-5 py-2.5 text-sm medium rounded-lg text-white bg-primary hover:opacity-90 transition-colors disabled:bg-primary-disabled disabled:cursor-not-allowed"
        >
          Export {module}
        </button>
        {module === 'orders' && (
          <button
            type="button"
            onClick={exportFoodItems}
            disabled={!filter.from || !filter.till}
            className="px-5 py-2.5 text-sm medium rounded-lg text-primary bg-white border border-primary hover:bg-primary-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Export products
          </button>
        )}
      </div>
    </div>
  );
};
